import { useState } from 'react';
import { FilterTabs, type FilterStatus } from './FilterTabs';
import { ProjectCard } from './ProjectCard';
import { useAuth } from '../../contexts/AuthContext';
import type { Project } from '../../types';

interface ProjectListProps {
  projects: Project[];
}

export function ProjectList({ projects }: ProjectListProps) {
  const { user } = useAuth();
  const [filter, setFilter] = useState<FilterStatus>('all');

  const isPrivilegedUser = user?.role === 'admin' || user?.role === 'reviewer';

  // Available filters based on role
  const availableFilters: FilterStatus[] = isPrivilegedUser
    ? ['all', 'mine', 'drafts', 'review', 'complete']
    : ['all', 'drafts', 'review', 'complete'];

  // Reset filter if current filter is not available for this role
  const effectiveFilter = availableFilters.includes(filter) ? filter : 'all';

  const filteredProjects = projects.filter((project) => {
    if (effectiveFilter === 'all') return true;
    if (effectiveFilter === 'mine') return user && project.submitter_id === user.email;
    if (effectiveFilter === 'drafts') return project.status === 'DRAFT';
    if (effectiveFilter === 'review') return project.status === 'AI_EVALUATED' || project.status === 'EVALUATION_FAILED' || project.status === 'UNDER_REVIEW';
    if (effectiveFilter === 'complete') return project.status === 'CONSENSUS_REACHED';
    return true;
  });

  const counts: Record<FilterStatus, number> = {
    all: projects.length,
    mine: user ? projects.filter((p) => p.submitter_id === user.email).length : 0,
    drafts: projects.filter((p) => p.status === 'DRAFT').length,
    review: projects.filter((p) => p.status === 'AI_EVALUATED' || p.status === 'EVALUATION_FAILED' || p.status === 'UNDER_REVIEW').length,
    complete: projects.filter((p) => p.status === 'CONSENSUS_REACHED').length,
  };

  return (
    <div>
      <FilterTabs
        activeFilter={effectiveFilter}
        onFilterChange={setFilter}
        counts={counts}
        availableFilters={availableFilters}
      />

      <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredProjects.map((project) => (
          <ProjectCard key={project.id} project={project} />
        ))}
      </div>

      {filteredProjects.length === 0 && (
        <div className="mt-12 text-center">
          <p className="text-wolf-500">No initiatives found</p>
        </div>
      )}
    </div>
  );
}
